import type { ReactNode } from "react";

/**
 * Shared all-confirm gate footer (T-D4) — the 확인 button + advance banner are
 * near-identical across the race-end (`RaceRecapView`) and round-end
 * (`RoundRecapView`) recaps. Extracted so both reuse one implementation.
 *
 * The per-player tracker chips that used to live here were absorbed into the
 * shared rail (TopBanner ready mode) by D3/PR-3+PR-4 — assert against
 * `[data-testid="horror-banner-player"][data-mode="ready"][data-is-confirmed]`
 * instead of the removed `recap-confirm-tracker`/`round-recap-tracker` ids.
 */

interface ConfirmFooterProps {
  readonly readOnly: boolean;
  readonly allConfirmed: boolean;
  readonly iConfirmed: boolean;
  /** Gate the button until the recap animation finishes (round-recap). Race-recap
   * leaves it enabled (default true). */
  readonly enabled?: boolean;
  /** Button label while gated (enabled=false) — round-recap "연출 재생 중…". */
  readonly pendingLabel?: string;
  readonly advanceMsg: string;
  readonly onConfirm: () => void;
  readonly btnTestId: string;
  readonly advanceMsgTestId: string;
  /**
   * Optional slot rendered ABOVE the confirm button inside the same footer
   * surface (PR-4) — used by RaceRecapView to host the 3-stage tab bar at the
   * wireframe-canonical bottom position. Omitted = no slot (RoundRecapView).
   */
  readonly topSlot?: ReactNode;
}

export function ConfirmFooter({
  readOnly,
  allConfirmed,
  iConfirmed,
  enabled = true,
  pendingLabel = "확인",
  advanceMsg,
  onConfirm,
  btnTestId,
  advanceMsgTestId,
  topSlot,
}: ConfirmFooterProps) {
  const muted = iConfirmed || !enabled;
  return (
    <footer
      className="px-4 py-3"
      style={{
        borderTop: "1px solid var(--cn-horror-surface-border)",
        backgroundColor: "var(--cn-horror-surface-secondary)",
        paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))",
      }}
    >
      {topSlot}
      {!readOnly && !allConfirmed && (
        <button
          type="button"
          data-testid={btnTestId}
          data-done={iConfirmed}
          onClick={onConfirm}
          disabled={muted}
          className="w-full rounded-xl py-3 font-extrabold text-[15px]"
          style={
            muted
              ? {
                  backgroundColor: "transparent",
                  color: "var(--cn-horror-text-muted)",
                  border: "1px solid var(--cn-horror-surface-border)",
                  cursor: enabled ? "default" : "not-allowed",
                  opacity: enabled ? 1 : 0.6,
                }
              : {
                  backgroundColor: "var(--cn-horror-p1)",
                  color: "#fff",
                  border: "none",
                  boxShadow: "0 0 14px var(--cn-horror-p1)",
                  cursor: "pointer",
                }
          }
        >
          {iConfirmed
            ? "확인 완료 — 다른 플레이어 대기 중"
            : enabled
              ? "확인"
              : pendingLabel}
        </button>
      )}
      {allConfirmed && (
        <div
          data-testid={advanceMsgTestId}
          className="flex items-center justify-center gap-2 py-1 font-bold text-[13px]"
          style={{ color: "var(--cn-horror-finished-flash)" }}
        >
          {advanceMsg}
        </div>
      )}
    </footer>
  );
}
